// TextEditor — the Text block's settings pane, restyled to the WL-011 framework.
// WL-011 (Fable Hail Mary Wave 2). Deck: concepts/edit-wheel-flow/11 (basic) →
// 12 (advanced). Spec §2.2.
//
// BASIC   = the rich-text field itself (the block's single primary action) with
//           its formatting toolbar + N/20000 counter. NEVER hidden.
// ADVANCED= Heading Text style card, Paragraph Text style card, Line Spacing,
//           Padding.
//
// EW-014/015 validation: the body is capped at 20,000 chars (matches the live
// counter); over-length is surfaced inline via the shared validator, not saved
// silently. The AI Assistant is MOCKED (no real generation call — AI-spend rule).
//
// Controlled: `value` (text-block model) + `onChange(patch)`. The BODY rich-text
// surface is a SWAPPABLE slot: pass `renderRichField({ value, onChange, over,
// error })` to replace the default visual placeholder. The live craftjs wiring
// (Text/TextEditorSettings) injects the REAL Froala editor here via that slot
// (WL-011d), so the deck keeps the lightweight placeholder while the running
// builder authors byte-identical rich-text HTML.

import React from "react";
import styled from "styled-components";
import BlockEditorPane from "../BlockEditorPane";
import EditorSection from "../EditorSection";
import AdvancedOptions from "../AdvancedOptions";
import { TypeStyleCard, LabeledSlider, QuadStepper } from "../controls";
import { maxLength, validateField } from "../validation";
import { fontFamily, focusRing } from "../../../../theme/tokens";
import { fieldChrome, ErrorText } from "../EditorField";
import { INLINE_AI_ASSISTANT_ENABLED } from "./textEditorFlags";

const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const PURPLE = fieldChrome.accent;
const BORDER = fieldChrome.border;
const MAX = 20000;

const TextGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 6h14M5 6V4.8M19 6V4.8M12 6v13M9.5 19h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// A lightweight rich-text surface mirroring the live Froala field's chrome:
// a formatting toolbar (visual) + an editable body + a counter. In production
// the craftjs wrapper mounts the real Froala editor here.
const FieldWrap = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 10px;
  overflow: hidden;
  font-family: ${fontFamily};
`;
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px;
  border-bottom: 1px solid ${BORDER};
  background: #fbfbfc;
`;
const TBtn = styled.button`
  min-width: 44px;
  height: 44px;
  padding: 0 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(p) => (p.$accent ? PURPLE : INK)};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #f0eafd; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;
const Editable = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: none;
  padding: 12px;
  resize: vertical;
  font-family: ${fontFamily};
  font-size: 14px;
  line-height: 1.5;
  color: ${INK};
  box-sizing: border-box;
  &:focus-visible { outline: none; box-shadow: inset 0 0 0 2px #ede4fe; }
  &::placeholder { color: ${fieldChrome.placeholder}; }
`;
const Counter = styled.div`
  text-align: right;
  padding: 4px 10px;
  font-family: ${fontFamily};
  font-size: 11px;
  color: ${(p) => (p.$over ? fieldChrome.error : MUTED)};
  border-top: 1px solid ${BORDER};
`;
// Body validation error uses the shared <ErrorText> (single field-error source).

// Real formatting controls (the placeholder mirrors the live Froala toolbar).
const FORMATTING = ["B", "I", "U", "S", "•", "1.", "🔗"];

const DEFAULT = {
  body: "",
  heading: { color: "#123051", font: "DM Sans", size: 20 },
  paragraph: { color: "#123051", font: "DM Sans", size: 16 },
  lineSpacing: 1.15,
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  applyPaddingAll: false,
};

// Default BODY rich-text surface: the visual placeholder (fake toolbar + textarea
// + counter). Used by the design deck / standalone renders. The live builder
// overrides this via the `renderRichField` slot with the real Froala editor.
const defaultRichField = ({ value, onChange, over, error }) => {
  // The inline "AI Assistant" button is MOCKED (no real generation call —
  // AI-spend rule). It is flag-gated OFF (NEMESIS AI-04) so a non-functional AI
  // control is never exposed; it reappears only when a real inline-AI impl
  // exists (REACT_APP_INLINE_AI_ASSISTANT_ENABLED flipped on). Read at render
  // time so the gate is honoured live. The live builder never hits this path —
  // it swaps in the real Froala editor via the renderRichField slot.
  const toolbar = INLINE_AI_ASSISTANT_ENABLED ? [...FORMATTING, "AI"] : FORMATTING;
  return (
  <>
    <FieldWrap>
      <Toolbar role="toolbar" aria-label="Text formatting">
        {toolbar.map((t) => (
          <TBtn
            key={t}
            type="button"
            $accent={t === "AI"}
            aria-label={t === "AI" ? "AI Assistant (mocked)" : `Format ${t}`}
            title={t === "AI" ? "AI Assistant" : t}
          >
            {t}
          </TBtn>
        ))}
      </Toolbar>
      <Editable
        aria-label="Text content"
        data-testid="text-editor-body"
        aria-invalid={!!error}
        placeholder="Write your text…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <Counter $over={over}>
        {String(value || "").length.toLocaleString()}/{MAX.toLocaleString()}
      </Counter>
    </FieldWrap>
    {error && <ErrorText role="alert">{error}</ErrorText>}
  </>
  );
};

const TextEditor = ({
  value = DEFAULT,
  onChange = () => {},
  segmentName,
  onBack,
  status = "ready",
  onRetry,
  errorMessage,
  renderRichField = defaultRichField,
}) => {
  const v = { ...DEFAULT, ...value };
  const patch = (p) => onChange({ ...v, ...p });
  const bodyError = validateField(v.body, [
    maxLength(MAX, `Text is too long — keep it under ${MAX.toLocaleString()} characters.`),
  ]);
  const over = String(v.body || "").length > MAX;

  const patchSide = (side, next) => {
    if (v.applyPaddingAll) patch({ padding: { top: next, right: next, bottom: next, left: next } });
    else patch({ padding: { ...v.padding, [side]: next } });
  };

  return (
    <BlockEditorPane
      blockName="Text"
      segmentName={segmentName}
      glyph={<TextGlyph />}
      onBack={onBack}
      status={status}
      onRetry={onRetry}
      errorMessage={errorMessage}
    >
      <EditorSection heading="Basic">
        {renderRichField({
          value: v.body,
          onChange: (body) => patch({ body }),
          over,
          error: bodyError,
        })}
      </EditorSection>

      <AdvancedOptions>
        <TypeStyleCard
          title="Heading Text"
          style={v.heading}
          onChange={(s) => patch({ heading: s })}
        />
        <TypeStyleCard
          title="Paragraph Text"
          style={v.paragraph}
          onChange={(s) => patch({ paragraph: s })}
        />
        <LabeledSlider
          label="Line Spacing"
          value={v.lineSpacing}
          min={1}
          max={2}
          step={0.05}
          onChange={(n) => patch({ lineSpacing: n })}
        />
        <QuadStepper
          label="Padding"
          value={v.padding}
          onChange={patchSide}
          applyAll={v.applyPaddingAll}
          onApplyAll={(b) => patch({ applyPaddingAll: b })}
        />
      </AdvancedOptions>
    </BlockEditorPane>
  );
};

export default TextEditor;
